import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { DAZE } from '../../../constants/measure';
import { MODE, SCROLL } from '../../../constants/assessmentItemType';
import AudioContainer from '../../../common/audio/audioContainer';
import PassageLayout from '../../../common/layouts/passageLayout';
import ClozeText from '../../../common/components/ClozeText';
import GoButton from '../../../common/components/GoButton';
import TextPaneScroll from '../../../common/textPaneScroll/TextPaneScroll';
import { SCROLL_ANIMATION_DURATION } from '../../../constants/slider';
import { getScrollStepSize } from '../../dazeUtils';
import { ApolloContext } from '../../../ApolloContextProvider';
import { ARIA_LABELS } from '../../../constants/text';
import { DEFAULT_FONT_SIZE_SCALE } from '../../../constants/accessibility';

class DazeModelScrollView extends React.Component {
    constructor(props) {
        super(props);
        this.getScrollableContent = this.getScrollableContent.bind(this);
        this.onScrollDownButtonClick = this.onScrollDownButtonClick.bind(this);
        this.isScrollModelPage = window.location.pathname && window.location.pathname.includes(`/${SCROLL}`) || false;
    }

    componentDidMount() {
        if (!this.props.isDataFetched) {
            this.props.fetchContent();
        } else {
            this.props.initModelScrollScreen();
            if (this.props.screenMode === MODE.ACTIVE) {
                this.props.activeScript();
            } else if (this.props.screenMode === MODE.READY) {
                this.props.activateNextButton();
                // use setTimeout for scroll to make OK button visible too.
                setTimeout(() => { this.scrollableView.scrollToBottom(this.props.scrollDuration); }, 100);
                this.props.readyScript();
            }
        }
    }

    componentDidUpdate(prevProps) {
        const { fontSizeScaleValue } = this.context;
        if (prevProps.accessibilityFontSize !== fontSizeScaleValue) {
            if (this.props.isScrollButtonClicked) {
                if (this.scrollableView) {
                    this.scrollableView.scrollToBottom(SCROLL_ANIMATION_DURATION);
                }
            }
        }
        if (!prevProps.isDataFetched && this.props.isDataFetched) {
            this.props.initModelScrollScreen();
            if (prevProps.screenMode === MODE.ACTIVE) {
                this.props.activeScript();
            } else if (prevProps.screenMode === MODE.READY) {
                this.props.readyScript();

                // TODO: investigate how to get rid of setTimeout
                // next setTimeout fixes a defect where page doesn't scroll to the bottom
                setTimeout(() => { this.scrollableView.scrollToBottom(SCROLL_ANIMATION_DURATION); }, 100);
            }
        } else if (prevProps.isDataFetched && prevProps.screenMode === MODE.ACTIVE
            && this.props.screenMode === MODE.READY) {
            setTimeout(() => { this.scrollableView.scrollToBottom(prevProps.scrollDuration); }, 100);
            this.props.readyScript();
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
    }

    onScrollDownButtonClick() {
        if (!this.props.isScrollButtonClicked && this.props.screenMode === MODE.ACTIVE) {
            this.props.setScreenMode(MODE.READY);
        }
    }

    getScrollableContent() {
        const titleSegment = (<h1 key="text-title">{this.props.title}</h1>);

        const textSegment = (
            <div className="lined-text">
                {this.props.paragraphs.map((paragraph) => (
                    <ClozeText
                        isTabIndexActive={!this.isScrollModelPage}
                        key={paragraph.id}
                        text={paragraph.text}
                        questions={this.props.questions}
                        selectedResponses={this.props.selectedResponses}
                    />
                ))}
            </div>
        );
        const buttonSegment = this.props.isNextButtonActive
            ? (
                <GoButton
                    isEnabled={!this.props.isScreenBlocked}
                    onClick={this.props.onNext}
                    disabled={this.props.isOkButtonGreyedOut}
                    customTabIndex="3"
                    ariaLabel={ARIA_LABELS.GO_TO_NEXT_STEP}
                />
            ) : null;
        return [titleSegment, textSegment, buttonSegment];
    }

    render() {
        const { fontSizeScaleValue } = this.context;
        return (
            <PassageLayout
                class={classNames(`${DAZE}-measure`, { 'is-extended-view': this.props.isExtendedView })}
                isScreenBlocked={this.props.isScreenBlocked}
                isGreyedOut={this.props.isGreyedOut}>
                <AudioContainer />
                <div className="text-pane">
                    <div className="daze-text">
                        <div className="content">
                            <TextPaneScroll
                                ref={(node) => {
                                    this.scrollableView = node;
                                }}
                                scrollToSection="segment-part-one"
                                scrollStep={getScrollStepSize(this.props.isExtendedView, fontSizeScaleValue)}
                                isEnabled={this.props.isDataFetched}
                                content={this.getScrollableContent()}
                                isScrollDownButtonHighlighted={this.props.isDownButtonHighlighted}
                                onScrollDownButtonClick={this.onScrollDownButtonClick}
                                isTabIndexDisabled={this.props.isGreyedOut || this.props.isScreenBlocked}
                                isDraggerVisible={this.props.screenMode === MODE.ACTIVE}
                                isScrollModelPage={this.isScrollModelPage}
                            />
                        </div>
                    </div>
                </div>
            </PassageLayout>
        );
    }
}

DazeModelScrollView.propTypes = {
    accessibilityFontSize:PropTypes.number,
    isDataFetched: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isGreyedOut: PropTypes.bool,
    isNextButtonActive: PropTypes.bool,
    isDownButtonHighlighted: PropTypes.bool,
    isScrollButtonClicked: PropTypes.bool,
    isOkButtonGreyedOut: PropTypes.bool,
    screenMode: PropTypes.string.isRequired,
    scrollDuration: PropTypes.number,
    title: PropTypes.string,
    paragraphs: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
    })),
    questions: PropTypes.array,
    selectedResponses: PropTypes.object,
    activeScript: PropTypes.func,
    fetchContent: PropTypes.func,
    initModelScrollScreen: PropTypes.func,
    readyScript: PropTypes.func,
    setScreenMode: PropTypes.func,
    onNext: PropTypes.func,
    cleanup: PropTypes.func,
    activateNextButton: PropTypes.func,
    isExtendedView: PropTypes.bool,
};

DazeModelScrollView.defaultProps = {
    accessibilityFontSize: DEFAULT_FONT_SIZE_SCALE,
    isDataFetched: false,
    isScreenBlocked: false,
    isGreyedOut: false,
    isNextButtonActive: false,
    isDownButtonHighlighted: false,
    isScrollButtonClicked: false,
    isOkButtonGreyedOut: false,
    scrollDuration: SCROLL_ANIMATION_DURATION,
    title: '',
    paragraphs: [],
    questions: [],
    selectedResponses: {},
    activeScript: () => {},
    fetchContent: () => {},
    initModelScrollScreen: () => {},
    readyScript: () => {},
    setScreenMode: () => {},
    onNext: () => {},
    cleanup: () => {},
    activateNextButton: () => {},
    isExtendedView: false,
};

DazeModelScrollView.contextType = ApolloContext;

export default DazeModelScrollView;
